/**
 * Design tokens: the only place where colours live (GOLDEN-RULE.md, rules 2 and 3).
 * Components use the CSS variables, e.g. `var(--text)`. `test/tokens.test.ts` checks
 * every text/background pair below for WCAG AA contrast in both themes.
 */

export type Palette = Record<string, string>;

export const light: Palette = {
  bg: '#f5f7fa',
  surface: '#ffffff',
  'surface-2': '#f2f4f7',
  'surface-3': '#eaecf0',
  border: '#e5e7eb',
  'border-strong': '#d0d5dd',
  text: '#172033',
  'text-2': '#475467',
  'text-3': '#5d6679',
  primary: '#2563eb',
  'primary-hover': '#1d4ed8',
  'primary-ink': '#ffffff',
  'primary-soft': '#eff4ff',
  'primary-soft-ink': '#1e40af',
  focus: '#2563eb',
  ok: '#15803d',
  'ok-soft': '#e8f7ee',
  'ok-ink': '#ffffff',
  warn: '#a14a05',
  'warn-soft': '#fef4e2',
  danger: '#c81e1e',
  'danger-soft': '#fef5f5',
  'danger-ink': '#ffffff',
  info: '#1d4ed8',
  'info-soft': '#eaf1ff',
  neutral: '#475467',
  'neutral-soft': '#f2f4f7',
  overlay: '#10182880',
  /** The side navigation is dark in both themes, so the work area reads as the page. */
  nav: '#101828',
  'nav-2': '#1d2a42',
  'nav-ink': '#a3acbd',
  'nav-on': '#ffffff',
  'nav-border': '#1d2939',
  /** The mark: a three-colour sweep used on the logo and nothing else. */
  'brand-1': '#3b82f6',
  'brand-2': '#2563eb',
  'brand-3': '#38bdf8',
  /** The ERP PRO cube (top, left and right faces) on its navy tile; the same in both themes. */
  'logo-top': '#93c5fd',
  'logo-left': '#3b82f6',
  'logo-right': '#ffffff',
  'logo-tile': '#17243a',
  'logo-tile-2': '#101828',
  /** The sign-in page's branding panel and its globe: the same deep navy in both themes. */
  'login-bg': '#07172f',
  'login-bg-2': '#0a2a5c',
  'login-glow': '#1d4ed8',
  'login-ink': '#ffffff',
  'login-muted': '#b4c0d4',
  'login-cyan': '#22d3ee',
  'login-cyan-2': '#38bdf8',
  'login-cyan-line': '#22d3ee59',
  'login-cyan-faint': '#22d3ee1f',
  'login-tile': '#0d2445',
  'login-line': '#1c3a64',
  'globe-ocean': '#020d22',
  'globe-land': '#0a2850',
  'globe-relief': '#24578f',
  'globe-lights': '#ffd58f',
  'globe-atmo': '#3ba7ff',
  'globe-arc': '#22d3ee',
  /** Chart series and solid status fills (dots, bars, rings), never used for text. */
  'chart-1': '#2563eb',
  'chart-2': '#0ea5e9',
  'chart-3': '#16a34a',
  'chart-4': '#f59e0b',
  'chart-5': '#8b5cf6',
  'chart-6': '#94a3b8',
  'ok-fill': '#16a34a',
  'warn-fill': '#f59e0b',
  'danger-fill': '#dc2626',
  /** Receipt paper: white with black ink in both themes, as it prints. */
  paper: '#ffffff',
  'paper-ink': '#000000',
  'paper-rule': '#5b5b5b',
  'tone-blue': '#e5eeff',
  'tone-blue-ink': '#1e48b8',
  'tone-green': '#e2f5e9',
  'tone-green-ink': '#14683a',
  /** Social networks' own colours, for their small round marks (white letter on top). */
  'net-facebook': '#1877f2',
  'net-instagram': '#d6249f',
  'net-instagram-2': '#fd5949',
  'net-linkedin': '#0a66c2',
  'net-x': '#111111',
  'net-ink': '#ffffff',
  'tone-orange': '#fff0dd',
  'tone-orange-ink': '#9a4a00',
  'tone-purple': '#f0e8ff',
  'tone-purple-ink': '#5b2db0',
  'tone-red': '#ffe6e3',
  'tone-red-ink': '#a32418',
  'tone-teal': '#ddf5f2',
  'tone-teal-ink': '#0d625a',
  'tone-yellow': '#fbf0c4',
  'tone-yellow-ink': '#6b4e00',
  'tone-grey': '#e7eaef',
  'tone-grey-ink': '#374152',
};

export const dark: Palette = {
  bg: '#0d1524',
  surface: '#152036',
  'surface-2': '#1b2840',
  'surface-3': '#23324d',
  border: '#24324b',
  'border-strong': '#37486a',
  text: '#f2f4f7',
  'text-2': '#c3cad6',
  'text-3': '#98a2b3',
  primary: '#6c9cf8',
  'primary-hover': '#8db2fb',
  'primary-ink': '#0b1220',
  'primary-soft': '#1a2c52',
  'primary-soft-ink': '#c7d8fe',
  focus: '#8db2fb',
  ok: '#4ade80',
  'ok-soft': '#0f2c1d',
  'ok-ink': '#06140d',
  warn: '#fbbf24',
  'warn-soft': '#33260b',
  danger: '#f87171',
  'danger-soft': '#3a1618',
  'danger-ink': '#0b1220',
  info: '#7db0ff',
  'info-soft': '#16264a',
  neutral: '#c3cad6',
  'neutral-soft': '#1b2840',
  overlay: '#020617b3',
  /** The side navigation is dark in both themes, so the work area reads as the page. */
  nav: '#0a101c',
  'nav-2': '#1b2840',
  'nav-ink': '#98a2b3',
  'nav-on': '#ffffff',
  'nav-border': '#172238',
  /** The mark: a three-colour sweep used on the logo and nothing else. */
  'brand-1': '#60a5fa',
  'brand-2': '#3b82f6',
  'brand-3': '#38bdf8',
  /** The ERP PRO cube (top, left and right faces) on its navy tile; the same in both themes. */
  'logo-top': '#93c5fd',
  'logo-left': '#3b82f6',
  'logo-right': '#ffffff',
  'logo-tile': '#17243a',
  'logo-tile-2': '#101828',
  /** The sign-in page's branding panel and its globe: the same deep navy in both themes. */
  'login-bg': '#07172f',
  'login-bg-2': '#0a2a5c',
  'login-glow': '#1d4ed8',
  'login-ink': '#ffffff',
  'login-muted': '#b4c0d4',
  'login-cyan': '#22d3ee',
  'login-cyan-2': '#38bdf8',
  'login-cyan-line': '#22d3ee59',
  'login-cyan-faint': '#22d3ee1f',
  'login-tile': '#0d2445',
  'login-line': '#1c3a64',
  'globe-ocean': '#020d22',
  'globe-land': '#0a2850',
  'globe-relief': '#24578f',
  'globe-lights': '#ffd58f',
  'globe-atmo': '#3ba7ff',
  'globe-arc': '#22d3ee',
  /** Chart series and solid status fills (dots, bars, rings), never used for text. */
  'chart-1': '#6c9cf8',
  'chart-2': '#38bdf8',
  'chart-3': '#4ade80',
  'chart-4': '#fbbf24',
  'chart-5': '#a78bfa',
  'chart-6': '#64748b',
  'ok-fill': '#4ade80',
  'warn-fill': '#fbbf24',
  'danger-fill': '#f87171',
  /** Receipt paper: white with black ink in both themes, as it prints. */
  paper: '#ffffff',
  'paper-ink': '#000000',
  'paper-rule': '#5b5b5b',
  'tone-blue': '#16244d',
  'tone-blue-ink': '#a9c1ff',
  'tone-green': '#122e1f',
  'tone-green-ink': '#8fe2b1',
  /** Social networks' own colours, for their small round marks (white letter on top). */
  'net-facebook': '#1877f2',
  'net-instagram': '#d6249f',
  'net-instagram-2': '#fd5949',
  'net-linkedin': '#0a66c2',
  'net-x': '#111111',
  'net-ink': '#ffffff',
  'tone-orange': '#3a2510',
  'tone-orange-ink': '#ffc487',
  'tone-purple': '#29194a',
  'tone-purple-ink': '#d3bcff',
  'tone-red': '#3b1715',
  'tone-red-ink': '#ffada5',
  'tone-teal': '#0e302d',
  'tone-teal-ink': '#86ded4',
  'tone-yellow': '#352c0c',
  'tone-yellow-ink': '#f2d675',
  'tone-grey': '#1f2a3f',
  'tone-grey-ink': '#c3ccdb',
};

/** Text-on-background pairs that must reach WCAG AA (4.5:1). */
export const CONTRAST_PAIRS: [text: string, background: string][] = [
  ['text', 'bg'],
  ['text', 'surface'],
  ['paper-ink', 'paper'],
  ['text', 'surface-2'],
  ['text', 'surface-3'],
  ['text-2', 'surface'],
  ['text-2', 'surface-2'],
  ['text-3', 'surface'],
  ['text-3', 'bg'],
  ['primary-ink', 'primary'],
  ['primary-ink', 'primary-hover'],
  ['primary-soft-ink', 'primary-soft'],
  ['primary', 'surface'],
  ['danger-ink', 'danger'],
  ['ok-ink', 'ok'],
  ['ok', 'ok-soft'],
  ['warn', 'warn-soft'],
  ['danger', 'danger-soft'],
  ['info', 'info-soft'],
  ['neutral', 'neutral-soft'],
  ['nav-ink', 'nav'],
  ['login-ink', 'login-bg'],
  ['login-muted', 'login-bg'],
  ['login-cyan', 'login-bg'],
  ['login-muted', 'login-tile'],
  ['login-ink', 'login-tile'],
  ['nav-on', 'nav'],
  ['nav-ink', 'nav-2'],
  ['nav-on', 'nav-2'],
  ['ok', 'surface'],
  ['warn', 'surface'],
  ['danger', 'surface'],
  ...(['blue', 'green', 'orange', 'purple', 'red', 'teal', 'yellow', 'grey'] as const).map(
    (t) => [`tone-${t}-ink`, `tone-${t}`] as [string, string],
  ),
];

/** Sizes shared by both themes. */
export const shared: Record<string, string> = {
  'font-sans':
    "'Inter', 'Segoe UI Variable', 'Segoe UI', system-ui, -apple-system, 'Helvetica Neue', Arial, sans-serif",
  'font-mono': "'JetBrains Mono', 'Cascadia Mono', ui-monospace, 'SFMono-Regular', Menlo, monospace",
  'text-xs': '0.75rem',
  'text-sm': '0.8125rem',
  'text-md': '0.9375rem',
  'text-lg': '1.125rem',
  'text-xl': '1.25rem',
  'text-2xl': '1.5rem',
  'text-3xl': '1.75rem',
  'tracking-tight': '-0.02em',
  /** Inter's weights: titles semibold/bold, never heavier (a premium look is calm). */
  'weight-regular': '400',
  'weight-medium': '500',
  'weight-semibold': '600',
  'weight-bold': '700',
  'space-1': '4px',
  'space-2': '8px',
  'space-3': '12px',
  'space-4': '16px',
  'space-5': '20px',
  'space-6': '24px',
  'space-8': '32px',
  'radius-sm': '6px',
  'radius-md': '8px',
  'radius-lg': '12px',
  'radius-xl': '16px',
  'radius-full': '999px',
  'touch': '44px',
  'touch-lg': '56px',
  'motion-fast': '150ms',
  'motion': '200ms',
  'ease': 'cubic-bezier(0.2, 0, 0, 1)',
  'shadow-1': '0 1px 2px rgb(var(--shadow-rgb) / 0.06)',
  'shadow-2': '0 4px 8px -2px rgb(var(--shadow-rgb) / 0.08), 0 2px 4px -2px rgb(var(--shadow-rgb) / 0.05)',
  'shadow-3': '0 20px 40px -8px rgb(var(--shadow-rgb) / 0.22), 0 8px 16px -6px rgb(var(--shadow-rgb) / 0.10)',
  /** A raised primary action carries its own colour, so it reads from across the room. */
  'shadow-primary': '0 1px 2px rgb(var(--primary-rgb) / 0.30)',
  'brand-sweep': 'linear-gradient(135deg, var(--brand-1) 0%, var(--brand-2) 55%, var(--brand-3) 100%)',
  /** The app icon's navy tile, behind the cube wherever the logo sits on a light surface. */
  'logo-sweep': 'linear-gradient(135deg, var(--logo-tile) 0%, var(--logo-tile-2) 100%)',
};

const block = (p: Palette) =>
  Object.entries(p)
    .map(([k, v]) => `--${k}: ${v};`)
    .join('\n  ');

/** The CSS for both themes: system preference by default, overridable with data-theme. */
export function tokensCss(): string {
  return `:root {
  ${block(shared)}
  ${block(light)}
  --shadow-rgb: 16 24 40;
  --primary-rgb: 37 99 235;
  color-scheme: light;
}
@media (prefers-color-scheme: dark) {
  :root:not([data-theme='light']) {
  ${block(dark)}
  --shadow-rgb: 0 0 0;
  --primary-rgb: 108 156 248;
  color-scheme: dark;
  }
}
:root[data-theme='dark'] {
  ${block(dark)}
  --shadow-rgb: 0 0 0;
  --primary-rgb: 108 156 248;
  color-scheme: dark;
}`;
}
